import React, { Fragment, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Divider,
  Skeleton,
  Typography,
  Box,
  ListItemButton,
  ListItem,
  List
} from '@mui/material'
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { ApolloError } from '@apollo/client'

import { QueryError, MuiLink } from 'components'
import { BaseSupportTicketFragmentFragment } from 'api/generated'
import { useThemeType } from 'context/theme'

export type SupportTicketsListProps = {
  supportTickets?: BaseSupportTicketFragmentFragment[]
  loading?: boolean
  error?: ApolloError
  refetch?(): void
}

const SupportTicketsList: React.FC<SupportTicketsListProps> = ({
  supportTickets,
  loading,
  error,
  refetch
}: SupportTicketsListProps) => {
  const { t } = useTranslation()
  const { dateFnsLocale } = useThemeType()

  const handleRefetchQuery = useCallback(() => {
    refetch?.()
  }, [refetch])

  return (
    <>
      {error ? (
        <QueryError
          error={error}
          loading={loading}
          refetch={handleRefetchQuery}
        />
      ) : loading ? (
        <Skeleton variant='rectangular' width='100%' height='40vh' />
      ) : supportTickets?.length ? (
        <List sx={{ height: '40vh', overflow: 'auto', width: '100%' }}>
          {supportTickets?.map(supportTicket => {
            return (
              <Fragment key={supportTicket?.id}>
                <ListItem secondaryAction={<ChevronRightIcon />} disablePadding>
                  <ListItemButton
                    component={MuiLink}
                    to={`/support-tickets/${supportTicket?.id}`}
                  >
                    <Box flex={1} display='flex' flexDirection='column'>
                      <Typography variant='subtitle2'>
                        {t(supportTicket?.type)}
                      </Typography>

                      <Typography variant='caption'>
                        {t(supportTicket?.status)}
                      </Typography>
                    </Box>

                    <Box
                      display='flex'
                      flexDirection='column'
                      alignItems='flex-end'
                    >
                      <Typography variant='subtitle2'>
                        {`${supportTicket?.user?.first_name || ''} ${
                          supportTicket?.user?.last_name || ''
                        }`}
                      </Typography>

                      <Typography variant='caption'>
                        {formatDistanceToNow(
                          new Date(supportTicket?.created_at),
                          {
                            addSuffix: true,
                            locale: dateFnsLocale
                          }
                        )}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
                <Divider component='li' />
              </Fragment>
            )
          })}
        </List>
      ) : (
        <Box
          height='40vh'
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          <Typography>{t('noRowsLabel')}</Typography>
        </Box>
      )}
    </>
  )
}

export default SupportTicketsList
